import React, { useState, useEffect, useCallback } from "react";
import {
  Text,
  Select,
  BlockStack,
  InlineStack,
  Button,
  Card,
} from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { fetch as fetchProductGroups } from "services/product-groups.service";
import { ProductGroup, ProductGroupItem } from "types/Product";
import { ProductLineFields } from "./ProductLineFields";

interface ProductGroupSelectProps {
  onProductGroupsChange?: (productGroups: ProductGroupItem[]) => void;
  initialProductGroups?: ProductGroupItem[];
  isRequired?: boolean;
}

const formatProductGroupLabel = (group: ProductGroup | null | undefined): string => {
  if (!group) return "";
  const code = group.code || "";
  const name = group.name || group.id || "";
  if (code && name) {
    return `${code} - ${name}`;
  }
  return name || code || "";
};

const ProductGroupSelect: React.FC<ProductGroupSelectProps> = ({
  onProductGroupsChange,
  initialProductGroups = [],
  isRequired = false,
}) => {
  const [selectedProductGroups, setSelectedProductGroups] = useState<ProductGroupItem[]>(
    initialProductGroups || []
  );
  const [productGroupSearchResults, setProductGroupSearchResults] = useState<ProductGroup[]>([]);
  const [productGroupDefaultList, setProductGroupDefaultList] = useState<ProductGroup[]>([]);
  const { companyId } = useParams();
  const { t } = useTranslation();

  useEffect(() => {
    const fetchInitialData = async () => {
      if (companyId) {
        const productGroups = await searchProductGroups(companyId);
        setProductGroupDefaultList(productGroups || []);
        setProductGroupSearchResults(productGroups || []);
      }
    };

    fetchInitialData();
  }, [companyId]);

  // propagate any changes in selectedProductGroups up to the parent
  useEffect(() => {
    onProductGroupsChange?.(selectedProductGroups);
  }, [selectedProductGroups, onProductGroupsChange]);

  const handleAddProductGroup = useCallback(
    (group: ProductGroup) => {
      setSelectedProductGroups((prev) => {
        if (!prev.some((pg) => pg.productGroup === group.id)) {
          const newGroup: ProductGroupItem = {
            productGroup: group.id,
            quantity: 1,
            price: 0,
            notes: "",
          };
          return [...prev, newGroup];
        }
        return prev;
      });
    },
    []
  );

  const handleRemoveProductGroup = useCallback(
    (productGroupId: string) => {
      setSelectedProductGroups((prev) => {
        return prev.filter((pg) => pg.productGroup !== productGroupId);
      });
    },
    []
  );

  const handleProductGroupQuantityChange = useCallback((productGroupId: string, quantity: any) => {
    setSelectedProductGroups((prev) =>
      prev.map((pg) =>
        pg.productGroup === productGroupId
          ? { ...pg, quantity }
          : pg
      )
    );
  }, []);

  const handleProductGroupPriceChange = useCallback((productGroupId: string, price: any) => {
    setSelectedProductGroups((prev) =>
      prev.map((pg) =>
        pg.productGroup === productGroupId
          ? { ...pg, price }
          : pg
      )
    );
  }, []);

  const handleProductGroupNotesChange = useCallback((productGroupId: string, notes: string) => {
    setSelectedProductGroups((prev) =>
      prev.map((pg) =>
        pg.productGroup === productGroupId
          ? { ...pg, notes }
          : pg
      )
    );
  }, []);

  const handleProductGroupSelectChange = useCallback(
    (value: string) => {
      if (value && value !== "") {
        const group =
          productGroupSearchResults.find((g) => g.id === value) ||
          productGroupDefaultList.find((g) => g.id === value);
        if (group) {
          handleAddProductGroup(group);
        }
      }
    },
    [productGroupSearchResults, productGroupDefaultList, handleAddProductGroup]
  );

  return (
    <BlockStack gap="400">
      <Select
        label={t("select_product_group")}
        options={[
          { label: t("select_product_group"), value: "" },
          ...productGroupSearchResults.map((group) => ({
            label: formatProductGroupLabel(group),
            value: group.id,
          })),
        ]}
        value=""
        onChange={handleProductGroupSelectChange}
        requiredIndicator={isRequired}
        error={isRequired && selectedProductGroups.length === 0}
      />
      {selectedProductGroups.length > 0 && (
        <BlockStack gap="400">
          <Text as="p" variant="bodyMd" fontWeight="medium">
            {t("selected_product_groups")}:
          </Text>
          {selectedProductGroups.map((pgItem) => {
            const group =
              productGroupSearchResults.find((g) => g.id === pgItem.productGroup) ||
              productGroupDefaultList.find((g) => g.id === pgItem.productGroup);
            const quantity = pgItem.quantity.toString();
            const price = pgItem.price.toString();

            return (
              <Card key={pgItem.productGroup}>
                <BlockStack gap="300">
                  <InlineStack align="space-between">
                    <Text as="p" fontWeight="bold">
                      {group ? formatProductGroupLabel(group) : pgItem.productGroup}
                    </Text>
                    <Button
                      variant="plain"
                      size="slim"
                      onClick={() => handleRemoveProductGroup(pgItem.productGroup)}
                      accessibilityLabel={t("remove")}
                    >
                      {t("remove")}
                    </Button>
                  </InlineStack>
                  <ProductLineFields
                    quantity={quantity}
                    price={price}
                    notes={pgItem.notes || ""}
                    onQuantityChange={(value) =>
                      handleProductGroupQuantityChange(pgItem.productGroup, value)
                    }
                    onPriceChange={(value) =>
                      handleProductGroupPriceChange(pgItem.productGroup, value)
                    }
                    onNotesChange={(value) =>
                      handleProductGroupNotesChange(pgItem.productGroup, value)
                    }
                  />
                </BlockStack>
              </Card>
            );
          })}
        </BlockStack>
      )}
    </BlockStack>
  );
};

const searchProductGroups = async (
  companyId: string,
  query?: string
): Promise<ProductGroup[] | undefined> => {
  const filter = { textSearch: query };
  try {
    const { productGroupsCollection } = await fetchProductGroups(companyId, filter);
    return productGroupsCollection.map((group: any) => ({
      id: group.id,
      name: group.name,
      code: group.code,
      description: group.description,
      products: group.products || [],
    }));
  } catch (e) {
    return undefined;
  }
};

export default ProductGroupSelect;
